import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import { ControlledInput } from "components/Card/ControlledInput";
import { TextInput } from "components/inputs";
import Tooltip from "components/Tooltip/Tooltip";
import React, { useEffect, useState } from "react";
import type {
  Control,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";

import type { LienFormValues } from "../../../LienTypes";

type EventNumbersSectionProps = {
  control: Control<LienFormValues>;
  getValues: UseFormGetValues<LienFormValues>;
  setValue: UseFormSetValue<LienFormValues>;
  handleFieldChange: (field: string | null, value: unknown) => Promise<void>;
};

const EventNumbersSection = ({
  control,
  getValues,
  setValue,
  handleFieldChange,
}: EventNumbersSectionProps) => {
  const [visibleEventNumbers, setVisibleEventNumbers] = useState(1);

  // initialize visible event numbers based on existing data
  useEffect(() => {
    const eventNumbers = getValues("initial_lien_request_event_numbers");
    if (Array.isArray(eventNumbers) && eventNumbers.length > 0) {
      let highestUsed = 1;
      for (let i = eventNumbers.length - 1; i >= 0; i--) {
        const eventNumber = eventNumbers[i];
        if (
          eventNumber &&
          typeof eventNumber === "string" &&
          String(eventNumber).trim()
        ) {
          highestUsed = i + 1;
          break;
        }
      }
      setVisibleEventNumbers(Math.max(highestUsed, 1));
    }
  }, []);

  // get current event numbers as array of strings
  const getCurrentEventNumbers = (): string[] => {
    const eventNumbers = getValues("initial_lien_request_event_numbers");
    if (Array.isArray(eventNumbers)) {
      return eventNumbers.map((item) => {
        if (typeof item === "string") return item;
        return "";
      });
    }
    return [];
  };

  // handle adding a new event number
  const handleAddEventNumber = () => {
    setVisibleEventNumbers(Math.min(visibleEventNumbers + 1, 5));
  };

  // handle deleting an event number
  const handleDeleteEventNumber = async (indexToDelete: number) => {
    const currentEventNumbers = getCurrentEventNumbers();
    const newEventNumbers = currentEventNumbers.filter(
      (_, i) => i !== indexToDelete,
    );
    setValue(
      "initial_lien_request_event_numbers",
      newEventNumbers as unknown as Record<string, unknown>[],
    );
    await handleFieldChange(
      "initial_lien_request_event_numbers",
      newEventNumbers,
    );
    setVisibleEventNumbers(visibleEventNumbers - 1);
  };

  // check if current event number is filled
  const isEventNumberFilled = (index: number) => {
    const eventNumbers = getCurrentEventNumbers();
    return Boolean(eventNumbers[index] && eventNumbers[index].trim());
  };

  // render individual event number field
  const renderEventNumberField = (index: number) => {
    return (
      <React.Fragment key={index}>
        <ControlledInput
          control={control}
          name={`initial_lien_request_event_numbers.${index}`}
          label={`Event Number ${index + 1}`}
          errors={{}}
          onSave={async (value) => {
            const newEventNumbers = [...getCurrentEventNumbers()];
            while (newEventNumbers.length <= index) {
              newEventNumbers.push("");
            }
            newEventNumbers[index] = value as string;
            await handleFieldChange(
              "initial_lien_request_event_numbers",
              newEventNumbers,
            );
          }}
          componentType={TextInput}
        />
      </React.Fragment>
    );
  };

  return (
    <>
      <div className="header-primary flex jc-sb ai-c">
        Event Numbers
        <div className="flex gap-sm jc-end">
          {visibleEventNumbers > 1 && (
            <ButtonIcon
              kind="neutral"
              size="small"
              className="delete"
              onClick={() => handleDeleteEventNumber(visibleEventNumbers - 1)}
              type="button"
            >
              delete
            </ButtonIcon>
          )}
          <Tooltip
            content={
              visibleEventNumbers >= 5
                ? "Maximum of 5 event numbers allowed"
                : !isEventNumberFilled(visibleEventNumbers - 1)
                  ? "Complete current event number before adding more"
                  : "Add new event number"
            }
          >
            <ButtonIcon
              kind="neutral"
              size="small"
              onClick={handleAddEventNumber}
              type="button"
              disabled={
                visibleEventNumbers >= 5 ||
                !isEventNumberFilled(visibleEventNumbers - 1)
              }
            >
              add_circle
            </ButtonIcon>
          </Tooltip>
        </div>
      </div>
      {Array.from({ length: visibleEventNumbers }, (_, i) => i).map((index) => (
        <React.Fragment key={index}>
          {renderEventNumberField(index)}
        </React.Fragment>
      ))}
    </>
  );
};

export default EventNumbersSection;
